import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import { MutableEntity } from '../../common';
import type { CommandContext } from '../../common/context';
import { UniqueShortId } from '../../common/id';

import Project = AuthDomain.Object.Project;

const uuidGenerator = UniqueShortId.GetFactory(12);
/**
 * ソースシステム
 *
 * 顧客データの投入元システムを表すマスターエンティティ。
 * Customer や ImportTask の sourceSystem として参照される。
 */
export namespace SourceSystem {
  export type Id = Tagged<UniqueShortId, 'sourceSystemId'>;
  export namespace Id {
    export const Generate = () => uuidGenerator.generate() as Id;
    export function FromString(id: string): Id {
      return uuidGenerator.FromString(id) as Id;
    }
  }

  export type Props = {
    id: string;
    projectId: string;
    displayName: string;
    description: string | undefined;
    isActive: boolean;
  } & MutableEntity.Props;

  export const Construct = (props: Props): SourceSystem => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      displayName: props.displayName,
      description: props.description,
      isActive: props.isActive,
      ...MutableEntity.Construct(props),
    };
  };

  export namespace Create {
    export type Context = CommandContext;
    export type Props = Pick<
      SourceSystem.Props,
      | 'displayName' //
      | 'description'
      | 'isActive'
    >;
    export type Params = {
      context: Context;
      props: Props;
    };
  }

  /** 新規作成時は isActive=true で初期化 */
  export const Create = (params: Create.Params): SourceSystem => {
    const { context, props } = params;
    return Construct({
      id: Id.Generate(),
      projectId: context.projectId,
      ...props,
      ...MutableEntity.Create(context),
    });
  };

  export namespace Update {
    export type Props = Partial<
      Pick<
        SourceSystem.Props,
        | 'displayName' //
        | 'description'
      >
    >;
    export type Params = {
      current: SourceSystem;
      props: Props;
      context: CommandContext;
    };
  }

  export const Update = (params: Update.Params): SourceSystem => {
    const { current, props, context } = params;
    return {
      ...current,
      displayName: props.displayName ?? current.displayName,
      description: props.description !== undefined ? props.description : current.description,
      ...MutableEntity.Update({ cur: current, ...context }),
    };
  };
}
export type SourceSystem = MutableEntity & {
  projectId: Project.Id;
  id: SourceSystem.Id;

  /** 表示名 e.g., "A社CRM", "Salesforce" */
  displayName: string;
  description: string | undefined;
  isActive: boolean;
};
